import { faCirclePlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, Formik, FormikHelpers, getIn, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import ReactQuill from "react-quill";
import * as Yup from "yup";

import PlansApi from "../../api/PlansApi";
import FASvgIcon from "../../components/FASvgIcon";
import PageHeader from "../../components/PageHeader";
import ScrollToFirstError from "../../components/ScrollToFirstError";
import CardLoader from "../../components/content-loader/CardLoader";
import { NumericInputField, SelectInputField } from "../../components/fields";
import { ButtonSpinner } from "../../components/placeholders";
import { QuillModules, appConstants, publishStatusOptions } from "../../config/app-constants";
import { EToastvariant, IPlanAddEdit, ISFPlanForm } from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";

const INIT_UF_VALUES: ISFPlanForm = {
  published: "",
  featured: false,
  title: "",
  subTitle: "",
  pricingValue: "",
  pricingSubTitle: "",
  description: "",
  footerContent: "",
  pricingRule: [{ minFilesCompleted: "", pricePerFile: "" }],
  uUId: ""
};

const INITIAL_FORM_SCHEMA = {
  published: Yup.string().notRequired(),
  featured: Yup.boolean().required("Featured plan is required"),
  title: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Plan Title must be less than 255 characters")
    .required("Plan Title is required"),
  subTitle: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Plan Subtitle must be less than 255 characters")
    .notRequired(),
  pricingValue: Yup.string().trim().notRequired(),
  pricingSubTitle: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(255, "Pricing Subtitle must be less than 255 characters")
    .notRequired(),
  description: Yup.string().trim().notRequired(),
  footerContent: Yup.string().trim().notRequired(),
  pricingRule: Yup.array().of(
    Yup.object().shape({
      minFilesCompleted: Yup.string()
        .trim()
        .matches(appConstants.OTP_DIGIT, "Invalid Max Filings")
        .required("Max Filings is required"),
      pricePerFile: Yup.string().trim().required("Price Per Filing is required")
    })
  )
};

const SelfFilingsPlans = () => {
  const dispatch = useAppDispatch();
  const [isFetching, setIsFetching] = useState(true);
  const [initialFormValues, setInitialFormValues] = useState(INIT_UF_VALUES);
  const [initialFormSchema, setInitialFormSchema] = useState(
    Yup.object().shape(INITIAL_FORM_SCHEMA)
  );
  const [incomeSourceList, setIncomeSourceList] = useState<any[]>([]);

  useEffect(() => {
    getSFPlanDetails();
    getIncomeSourceList();
  }, []);

  const getIncomeSourceList = async () => {
    try {
      const plansApi = PlansApi.getInstance();
      const response = await plansApi.getIncomeSource();
      const _data = response.data;
      const _incomeSourceList = Object.keys(_data).map((key) => _data[key]);
      setIncomeSourceList(() => _incomeSourceList);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    }
  };

  const getSFPlanDetails = async () => {
    try {
      setIsFetching(true);
      const plansApi = PlansApi.getInstance();
      const { records, count } = await plansApi.getPlans({
        offset: 0,
        count: -1,
        orderBy: "createdAt",
        order: "DESC",
        type: "self"
      });
      const _data = records[0];
      const _selfFilingData = {
        published: _data?.publishedAt ? "true" : "false",
        featured: _data?.featured,
        title: _data?.title || "",
        subTitle: _data?.subTitle || "",
        pricingValue: _data?.pricingTitle || "",
        pricingSubTitle: _data?.pricingSubTitle || "",
        description: _data?.description || "",
        footerContent: _data?.footerContent || "",
        pricingRule: _data?.pricingRule,
        uUId: _data.uuid
      };
      setInitialFormValues(() => _selfFilingData);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsFetching(false);
    }
  };

  const handleAddEditFormSubmit = async (
    values: ISFPlanForm,
    { setSubmitting, resetForm }: FormikHelpers<ISFPlanForm>
  ) => {
    try {
      setSubmitting(true);
      let _msg = `${values?.title} added successfully`;
      let _uUId = initialFormValues?.uUId;

      let _values: IPlanAddEdit = {
        title: values.title,
        subTitle: values.subTitle,
        pricingTitle: values.pricingValue,
        pricingSubTitle: values.pricingSubTitle,
        description: values.description,
        footerContent: values.footerContent,
        pricingRule: values.pricingRule,
        featured: values.featured,
        published: values.published,
        incomeSources: incomeSourceList
      };

      if (!_uUId) {
        _values.type = "self";
      }

      const plansApi = PlansApi.getInstance();
      if (_uUId) {
        await plansApi.updatePlanById(_uUId, _values);
        _msg = `Changes applied to ${_values?.title} successfully`;
      } else {
        const data = await plansApi.addPlan(_values);
        _uUId = data?.uuid;
        resetForm();
      }
      getSFPlanDetails();
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: _msg,
          variant: EToastvariant.Success
        })
      );
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Container className="w-800">
        <PageHeader title="Self Filing Plans" />

        {isFetching ? (
          <CardLoader cardType="details" />
        ) : (
          <Formik
            enableReinitialize={true}
            initialValues={initialFormValues}
            validationSchema={initialFormSchema}
            onSubmit={handleAddEditFormSubmit}
          >
            {({
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              setTouched,
              validateForm,
              values,
              errors,
              touched,
              isSubmitting,
              submitCount,
              isValid
            }) => (
              <>
                <Form
                  onSubmit={async (event) => {
                    event.preventDefault();
                    await validateForm();
                    handleSubmit();
                  }}
                >
                  <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />
                  <Card className="form-card">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="inline-label">Status</Form.Label>
                        </Col>
                        <Col>
                          <Row className="align-items-center">
                            <Col sm={6}>
                              <SelectInputField
                                placeholder="Select status"
                                name="published"
                                menuZIndex={9}
                                value={
                                  values?.published
                                    ? {
                                        label: publishStatusOptions[values.published],
                                        value: values.published
                                      }
                                    : null
                                }
                                options={UtilService.convertToArray(publishStatusOptions)}
                                onChange={(selectedStatus) => {
                                  if (selectedStatus) {
                                    setFieldValue("published", selectedStatus.value);
                                  } else {
                                    setFieldValue("published", undefined);
                                  }
                                }}
                                onBlur={handleBlur("published")}
                                isInvalid={Boolean(errors.published) && touched.published}
                                errorMsg={errors.published}
                                formType="GENERAL"
                              />
                            </Col>
                            <Col sm={6}>
                              <Form.Group className="form-group">
                                <Form.Check
                                  role="button"
                                  type="checkbox"
                                  id="featured"
                                  className="align-items-center"
                                >
                                  <Form.Check.Input
                                    type="checkbox"
                                    name="featured"
                                    checked={values.featured}
                                    onChange={(e) => {
                                      setFieldValue("featured", e.target.checked);
                                    }}
                                    isInvalid={Boolean(errors.featured) && touched.featured}
                                  />
                                  <Form.Check.Label>Mark Most Popular</Form.Check.Label>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.featured}
                                  </Form.Control.Feedback>
                                </Form.Check>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="mb-0">Plan Header</Form.Label>
                        </Col>
                        <Col>
                          <Row className="align-items-center">
                            <Col sm={6}>
                              <Form.Group className="form-group" controlId="title">
                                <Form.Label className="small-label required-field">
                                  Plan Title
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder=""
                                  maxLength={255}
                                  name="title"
                                  value={values.title}
                                  onBlur={handleBlur("title")}
                                  onChange={handleChange("title")}
                                  isInvalid={Boolean(errors.title) && touched.title}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.title}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col sm={6}>
                              <Form.Group className="form-group" controlId="subTitle">
                                <Form.Label className="small-label">Plan Subtitle</Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder=""
                                  maxLength={255}
                                  name="subTitle"
                                  value={values.subTitle}
                                  onBlur={handleBlur("subTitle")}
                                  onChange={handleChange("subTitle")}
                                  isInvalid={Boolean(errors.subTitle) && touched.subTitle}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.subTitle}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="mb-0">Pricing Details</Form.Label>
                        </Col>
                        <Col>
                          <Row className="align-items-center">
                            <Col sm={6}>
                              <Form.Group className="form-group" controlId="pricingValue">
                                <Form.Label className="small-label">Pricing Value</Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder=""
                                  name="pricingValue"
                                  value={values.pricingValue}
                                  onBlur={handleBlur("pricingValue")}
                                  onChange={handleChange("pricingValue")}
                                  isInvalid={Boolean(errors.pricingValue) && touched.pricingValue}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.pricingValue}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col sm={6}>
                              <Form.Group className="form-group" controlId="pricingSubTitle">
                                <Form.Label className="small-label">Pricing Subtitle</Form.Label>
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder=""
                                  maxLength={255}
                                  name="pricingSubTitle"
                                  value={values.pricingSubTitle}
                                  onBlur={handleBlur("pricingSubTitle")}
                                  onChange={handleChange("pricingSubTitle")}
                                  isInvalid={
                                    Boolean(errors.pricingSubTitle) && touched.pricingSubTitle
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.pricingSubTitle}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Form.Group className="form-group" controlId="description">
                            <Form.Label>Plan Description</Form.Label>
                            <ReactQuill
                              theme="snow"
                              className="h-250"
                              modules={QuillModules}
                              value={values.description}
                              onChange={handleChange("description")}
                              placeholder="Enter plan description"
                            />
                            {Boolean(errors.description) && touched.description && (
                              <span className="field-error invalid">{errors.description}</span>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col sm={4}>
                          <Form.Label className="inline-label">Plan footer info</Form.Label>
                        </Col>
                        <Col>
                          <Form.Group className="form-group" controlId="footerContent">
                            <Form.Control
                              type="text"
                              autoComplete="false"
                              placeholder=""
                              maxLength={255}
                              name="footerContent"
                              value={values.footerContent}
                              onBlur={handleBlur("footerContent")}
                              onChange={handleChange("footerContent")}
                              isInvalid={Boolean(errors.footerContent) && touched.footerContent}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.footerContent}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="my-5">
                        <Col>
                          <p className="leading-para text-primary fw-bold">
                            Self filing pricing breakup
                          </p>
                          <Table className="table-sf-form mb-5" border={0} responsive={true}>
                            <tbody>
                              <tr>
                                <td className="py-0">Max. filings</td>
                                <td className="py-0">Price per filing</td>
                                <td className="p-0"></td>
                              </tr>
                              <FieldArray name="pricingRule">
                                {({ push, remove, form }) => (
                                  <>
                                    {values?.pricingRule && values?.pricingRule?.length > 0
                                      ? values.pricingRule.map((_, index) => (
                                          <SFPricing
                                            key={`sFPricing-${index}`}
                                            index={index}
                                            onRemove={(itemIdx: number) => remove(itemIdx)}
                                          />
                                        ))
                                      : null}
                                    <tr>
                                      <td colSpan={2}>
                                        <Button
                                          variant="link"
                                          className="btn-add-link"
                                          onClick={() =>
                                            push({
                                              minFilesCompleted: "",
                                              pricePerFile: ""
                                            })
                                          }
                                        >
                                          <FASvgIcon icon={faCirclePlus} />
                                          Add more
                                        </Button>
                                      </td>
                                      <td></td>
                                    </tr>
                                  </>
                                )}
                              </FieldArray>
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                      <div className="form-btns-wrap">
                        {/* <Button
                          size="lg"
                          variant={uUId ? "outline-danger" : "outline-greyDark"}
                          className="btn-w-120"
                          onClick={() => {
                            uUId ? navigate(`/assisted-filings/${uUId}`) : navigate(-1);
                          }}
                        >
                          {isSubmitting ? <ButtonSpinner /> : "Cancel"}
                        </Button> */}
                        <Button
                          size="lg"
                          variant="primary"
                          className="btn-w-160"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <ButtonSpinner /> : "Save"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
        )}
      </Container>
    </>
  );
};

export default SelfFilingsPlans;

type SFPricingBreakupProps = {
  index: number;
  onRemove: (itemIdx: number) => void;
};

const SFPricing: React.FC<SFPricingBreakupProps> = (props: SFPricingBreakupProps) => {
  const { index, onRemove } = props;
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<ISFPlanForm>();

  return (
    <tr>
      <td>
        <NumericInputField
          showRightIcon={true}
          rightIcon="or more"
          groupTextClass="long-text"
          placeholder="0"
          name={`pricingRule.${index}.minFilesCompleted`}
          value={values.pricingRule[index]?.minFilesCompleted}
          onBlur={handleBlur(`pricingRule.${index}.minFilesCompleted`)}
          onChange={handleChange(`pricingRule.${index}.minFilesCompleted`)}
          isInvalid={
            Boolean(getIn(errors, `pricingRule.${index}.minFilesCompleted`)) &&
            getIn(touched, `pricingRule.${index}.minFilesCompleted`)
          }
          errorMsg={getIn(errors, `pricingRule.${index}.minFilesCompleted`)}
          formType="GENERAL"
        />
      </td>
      <td>
        <NumericInputField
          showLeftIcon={true}
          placeholder="0"
          name={`pricingRule.${index}.pricePerFile`}
          value={values.pricingRule[index]?.pricePerFile}
          onBlur={handleBlur(`pricingRule.${index}.pricePerFile`)}
          onChange={handleChange(`pricingRule.${index}.pricePerFile`)}
          isInvalid={
            Boolean(getIn(errors, `pricingRule.${index}.pricePerFile`)) &&
            getIn(touched, `pricingRule.${index}.pricePerFile`)
          }
          errorMsg={getIn(errors, `pricingRule.${index}.pricePerFile`)}
          formType="GENERAL"
        />
      </td>
      <td>
        <Button
          variant="transparent"
          className="icon-only sq-40 text-greyDark"
          onClick={() => onRemove(index)}
          disabled={values.pricingRule.length === 1}
        >
          <FASvgIcon icon={faTrash} />
        </Button>
      </td>
    </tr>
  );
};
